import { useRef, useState } from 'react';
import './selfie.css';
import ziniPeaceImg from '../../assets/zini_peace.png';

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GalleryIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="5" width="16" height="14" rx="2.4" stroke="#6c4ce8" strokeWidth="1.8" />
      <circle cx="9" cy="10" r="1.6" stroke="#6c4ce8" strokeWidth="1.6" />
      <path d="m5.5 16.5 4-4 3 3 3.2-3.6 3.8 4.6" stroke="#6c4ce8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M5 8.5A2.5 2.5 0 0 1 7.5 6h1.6l1.2-1.8h4.4L14.9 6h1.6A2.5 2.5 0 0 1 19 8.5v7A2.5 2.5 0 0 1 16.5 18h-9A2.5 2.5 0 0 1 5 15.5v-7Z" stroke="#f5a531" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="3.3" stroke="#f5a531" strokeWidth="1.8" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M12 15V4M12 4 7.5 8.5M12 4l4.5 4.5" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 15.5v2A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-2" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TOTAL_STEPS = 3;
const ACTIVE_STEP = 2;

export default function Selfie({ onBack, onGallery, onOpenCamera }) {
  const fileInputRef = useRef(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  function openGallery() {
    setSheetOpen(false);
    fileInputRef.current?.click();
  }

  function openCamera() {
    setSheetOpen(false);
    onOpenCamera?.();
  }

  function handleFilePicked(event) {
    const file = event.target.files?.[0];
    // Allow re-picking the same file next time.
    event.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onGallery?.(reader.result);
    reader.readAsDataURL(file);
  }

  return (
    <div className="selfie-screen">
      <div className="selfie-frame">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="selfie-file-input"
          onChange={handleFilePicked}
        />

        <div className="selfie-topbar">
          <button type="button" className="selfie-back-btn" aria-label="Go back" onClick={onBack}>
            <ArrowLeftIcon />
          </button>

          <div className="selfie-stepper">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
              <span
                key={n}
                className={'selfie-step-dot' + (n === ACTIVE_STEP ? ' selfie-step-dot-active' : '')}
              >
                {n}
              </span>
            ))}
          </div>

          <span className="selfie-topbar-spacer" />
        </div>

        <div className="selfie-body">
          <div className="selfie-hero">
            <span className="selfie-badge">{ACTIVE_STEP}</span>
            <div className="selfie-hero-text">
              <h1 className="selfie-title">
                Take a selfie with{' '}
                <span className="selfie-title-accent-teal">Zini</span>.
              </h1>
              {/* <h1 className="selfie-title">
                Take a <span className="selfie-title-accent-orange">selfie</span> with{' '}
                <span className="selfie-title-accent-teal">Zini</span>.
              </h1> */}
              <p className="selfie-subtitle">Strike a pose and click a selfie!</p>
            </div>
          </div>

          <div className="selfie-spotlight-wrap">
            <div className="selfie-spotlight">
              <span className="selfie-spotlight-ring" />
              <img src={ziniPeaceImg} alt="Zini mascot" className="selfie-mascot" />
            </div>
            <span className="selfie-mascot-tag">Zini is ready when you are</span>
          </div>
        </div>

        <div className="selfie-actions">
          <button type="button" className="selfie-btn-upload" onClick={() => setSheetOpen(true)}>
            <span className="selfie-btn-icon">
              <UploadIcon />
            </span>
            Upload a photo
          </button>
        </div>

        {sheetOpen && (
          <div className="selfie-sheet-overlay" onClick={() => setSheetOpen(false)}>
            <div className="selfie-sheet" onClick={(e) => e.stopPropagation()}>
              <span className="selfie-sheet-handle" />
              <p className="selfie-sheet-title">Add your selfie</p>

              <button type="button" className="selfie-sheet-option" onClick={openCamera}>
                <span className="selfie-sheet-option-icon selfie-sheet-option-icon-camera">
                  <CameraIcon />
                </span>
                <span className="selfie-sheet-option-text">
                  <span className="selfie-sheet-option-title">Camera</span>
                  <span className="selfie-sheet-option-sub">Take a new photo</span>
                </span>
              </button>

              <button type="button" className="selfie-sheet-option" onClick={openGallery}>
                <span className="selfie-sheet-option-icon selfie-sheet-option-icon-gallery">
                  <GalleryIcon />
                </span>
                <span className="selfie-sheet-option-text">
                  <span className="selfie-sheet-option-title">Gallery</span>
                  <span className="selfie-sheet-option-sub">Choose from your photos</span>
                </span>
              </button>

              <button type="button" className="selfie-sheet-cancel" onClick={() => setSheetOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
